import { Dispatch, SetStateAction, useContext, useEffect, useMemo } from 'react';
import { useMutation } from '@tanstack/react-query';

import { Volume } from '~/shared/types/api';
import { useXFetch } from '~/shared/utils/useXFetch';
import Switch from '~/shared/components/Switch';
import Typography from '~/shared/components/Typography';
import { DatafySwitchContext } from '~/shared/context/DatafySwitchProvider';

// import { DatafySwitchContext } from '../DatafySwitchProvider';

import {
  baseUrl,
  datafyErrorToast,
  isSwitchDisabled,
  isSwitchedOn,
  optmisticSwitchingInterval,
} from '../../helpers';

export default function OptimisticSwitch({
  volume,
  showLabel,
  setSelectedVolumes,
  switchSize = 'md',
  onError,
  isUserReadOnly,
}: {
  volume: Volume;
  showLabel?: boolean;
  setSelectedVolumes?: Dispatch<SetStateAction<string[]>>;
  switchSize?: 'sm' | 'md';
  onError?: (message: string) => void;
  isUserReadOnly?: boolean;
}) {
  const { volumeId } = volume;
  const api = useXFetch();

  const {
    datafySwitchVolumes,
    addDatafySwitchVolume,
    toggleDatafySwitchVolumeState,
    removeDatafySwitchVolume,
  } = useContext(DatafySwitchContext);

  const machineVolume = useMemo(
    () => datafySwitchVolumes.find((v) => v.volumeId === volumeId),
    [volumeId, datafySwitchVolumes]
  );

  const handleStateMachineUpdateOnSwitchChange = () => {
    const addVolumeState =
      machineVolume?.state === 'datafying' || isSwitchedOn(volume) ? 'undatafying' : 'datafying';

    if (machineVolume) {
      toggleDatafySwitchVolumeState(volumeId);
    } else {
      addDatafySwitchVolume(volumeId, addVolumeState);
    }
  };

  const { mutateAsync: datafyVolume } = useMutation({
    mutationKey: ['datafy-volume'],
    mutationFn: async (volumeId: Volume['volumeId']) => {
      handleStateMachineUpdateOnSwitchChange();
      const res = await api<unknown>(`${baseUrl}/${volumeId}/datafy`, {
        method: 'POST',
        body: JSON.stringify({}),
      });
      return res;
    },
    onError: (error) => {
      console.log('Datafy error response:', error);
      removeDatafySwitchVolume(volumeId);
      datafyErrorToast(volumeId, 'datafying');
      // Notify parent component of error
      onError?.('Failed to datafy volume');
    },
  });

  const { mutateAsync: unDatafyVolume } = useMutation({
    mutationKey: ['undatafy-volume'],
    mutationFn: async (volumeId: string) => {
      handleStateMachineUpdateOnSwitchChange();
      return await api<unknown>(`${baseUrl}/${volumeId}/undatafy`, {
        method: 'POST',
      });
    },
    onError: (error) => {
      console.log('Datafy error response:', error);
      removeDatafySwitchVolume(volumeId);
      datafyErrorToast(volumeId, 'undatafying');
      // Notify parent component of error
      onError?.('Failed to undatafy volume');
    },
  });

  // Add error handling in the timer check
  useEffect(() => {
    const interval = setInterval(() => {
      const now = new Date();
      datafySwitchVolumes.forEach((switchVolume) => {
        if (
          now.getTime() - switchVolume.timeStamp.getTime() >= optmisticSwitchingInterval &&
          switchVolume.volumeId === volumeId
        ) {
          if (
            (switchVolume?.state === 'datafying' && volume.monitoring === 'Monitored') ||
            (switchVolume?.state === 'undatafying' && volume.monitoring === 'Managed')
          ) {
            datafyErrorToast(volumeId, switchVolume.state);
            // Notify parent component of timeout error
            onError?.(
              `Operation timeout: ${switchVolume.state === 'datafying' ? 'datafying' : 'undatafying'}`
            );
          }
          console.log('removing in timer');
          removeDatafySwitchVolume(volumeId);
        }
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [datafySwitchVolumes, removeDatafySwitchVolume, volumeId, volume, onError]);

  async function onChange() {
    setSelectedVolumes?.((prev) => prev.filter((v) => v !== volumeId));
    if (
      (isSwitchedOn(volume) && machineVolume?.state !== 'undatafying') ||
      machineVolume?.state === 'datafying'
    ) {
      await unDatafyVolume(volumeId!);
    } else {
      await datafyVolume(volume.volumeId);
    }
  }

  const isEnabled = !isSwitchDisabled(volume) && !isUserReadOnly;

  return (
    <div className="w-fit flex gap-[12px] items-center">
      {showLabel ? (
        <Typography variant="p3" color={isEnabled ? undefined : 'text-[#949EAC]'}>
          {machineVolume?.state === 'datafying' ||
          (volume.monitoring === 'Migrating' && !machineVolume)
            ? 'Datafying'
            : (volume.monitoring === 'Unmigrating' && !machineVolume) ||
                machineVolume?.state === 'undatafying'
              ? 'Undatafying'
              : volume.monitoring === 'Monitored'
                ? 'Datafy'
                : 'Undatafy'}
        </Typography>
      ) : null}
      <Switch
        size={switchSize}
        checked={
          machineVolume?.state === 'undatafying'
            ? false
            : machineVolume?.state === 'datafying' || isSwitchedOn(volume)
        }
        onChange={() => (isEnabled ? onChange() : null)}
        className="z-10"
        disabled={
          !isEnabled ||
          (machineVolume?.state === 'undatafying' && volume.monitoring === 'Migrating') ||
          (machineVolume?.state === 'undatafying' &&
            volume.monitoring !== 'Migrating' &&
            typeof volume?.statusProgress !== 'number') ||
          (machineVolume?.state === 'datafying' && typeof volume?.statusProgress !== 'number')
        }
      />
    </div>
  );
}
